import React, { useEffect, useState } from 'react';
import Spinner from '../layouts/Spinner';
import { connect } from 'react-redux';
import {
    getDailyMovement,
    dailyMovementChecker,
    sortTableByColumn,
    getSelectedRows,
    setModal,
} from '../../redux/actions/daily_movement/daily_movement.actions.js';
import { formattedDate } from '../../Utils/Common';
import { Show, useAccess } from 'react-redux-permission';
import { Link } from 'react-router-dom';
import DailyMovementDateFilter from './dailyMovement.datefilter.global.js';
import DailyMovementViewModal from './dailyMovement.moda.view.js';
import SalesFilter from './dailyMovement.movementfilter.js';
import { NumberFormat } from '../../Services/_numberformat.service.js';
import * as XLSX from 'xlsx';

function DailyMovementTable({
    daily_movement: {
        daily_movement,
        sort_order_direction,
        pagination,
        loading,
        sort_order_name,
        set_row_checked,
        search,
        daily_movement_modal,
        movement_option, // Adding movement_option here
    },
    getDailyMovement,
    dailyMovementChecker,
    sortTableByColumn,
    setMUIPage,
    getSelectedRows,
    pageOrigin,
    muiPage,
    setModal,
}) {
    const { hasPermission } = useAccess();
    const canview = hasPermission('inv.dashboard.daily-movement.view');

    const [visibleColumns, setVisibleColumns] = useState({
        product_name: true,
        sku: true,
        brand_name: true,
        description: true,
        supplier_name: true,
        pos_qty: true,
        sales_order_qty: true,
        delivery_qty: true,
        total_qty: true,
        available_stocks: true,
        incoming_stocks: true,
        draft_incoming_stocks: true,
    });

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [isChecking, setIsChecking] = useState(true); // New state to manage spinner

    useEffect(() => {
        const fetchData = async () => {
            setIsChecking(true); // Start spinner
            await dailyMovementChecker(); // Ensure this function is asynchronous if it's not already
            setIsChecking(false); // Stop spinner after check is complete
            getDailyMovement(muiPage, null, pageOrigin);
        };

        fetchData();
    }, [getDailyMovement, muiPage, pageOrigin]);

    useEffect(() => {
        // Adjust the visibility of columns based on movement_option
        if (movement_option.value === 'all') {
            setVisibleColumns((prev) => ({
                ...prev,
                total_qty: true,
                pos_qty: true,
                sales_order_qty: true,
                delivery_qty: true,
            }));
        } else if (movement_option.value === 'pos') {
            setVisibleColumns((prev) => ({
                ...prev,
                pos_qty: true,
                sales_order_qty: false,
                delivery_qty: false,
                total_qty: false,
            }));
        } else if (movement_option.value === 'sales order') {
            setVisibleColumns((prev) => ({
                ...prev,
                pos_qty: false,
                sales_order_qty: true,
                delivery_qty: false,
                total_qty: false,
            }));
        } else if (movement_option.value === 'delivery') {
            setVisibleColumns((prev) => ({
                ...prev,
                pos_qty: false,
                sales_order_qty: false,
                delivery_qty: true,
                total_qty: false,
            }));
        }
    }, [movement_option]);

    // Calculate totals for each field
    const calculateTotals = () => {
        let totals = {
            pos_qty: 0,
            sales_order_qty: 0,
            delivery_qty: 0,
            total_qty: 0,
            available_stocks: 0,
            incoming_stocks: 0,
            draft_incoming_stocks: 0,
        };

        daily_movement.forEach((movement) => {
            totals.pos_qty += movement.pos_qty || 0;
            totals.sales_order_qty += movement.sales_order_qty || 0;
            totals.delivery_qty += movement.delivery_qty || 0;
            totals.total_qty += movement.total_qty || 0;
            totals.available_stocks += movement.available_stocks || 0;
            totals.incoming_stocks += movement.incoming_stocks || 0;
            totals.draft_incoming_stocks += movement.draft_incoming_stocks || 0;
        });

        return totals;
    };

    const totals = calculateTotals();

    const handleSort = (columnName) => {
        const direction =
            sort_order_name === columnName && sort_order_direction === 'asc'
                ? 'desc'
                : 'asc';
        sortTableByColumn(columnName, direction, pageOrigin);
    };

    const getSortIcon = (columnName) => {
        return (
            <i
                className={`la la-arrows-v ${
                    sort_order_name === columnName &&
                    sort_order_direction === 'asc'
                        ? 'rotate-180'
                        : ''
                }`}
                style={{ fontSize: '14px' }}
            ></i>
        );
    };

    const handleRowClick = (rowId) => {
        const selectedRow = daily_movement.find((row) => row._id === rowId);
        const products = selectedRow?.products || [];
        setModal('daily_movement_modal', products);
    };

    const handleDownloadExcel = () => {
        // Prepare the data for the Excel file
        const headers = Object.keys(visibleColumns)
            .filter((column) => visibleColumns[column])
            .map((column) => column.replace('_', ' '));

        const data = daily_movement.map((movement) =>
            Object.keys(visibleColumns)
                .filter((column) => visibleColumns[column])
                .map((column) =>
                    movement[column] !== null && movement[column] !== undefined
                        ? movement[column]
                        : 'N/A'
                )
        );

        // Calculate totals (same function as above)
        const totals = calculateTotals();

        // Add the totals row at the end of the data array
        const totalsRow = Object.keys(visibleColumns)
            .filter((column) => visibleColumns[column])
            .map((column) => {
                switch (column) {
                    case 'pos_qty':
                        return totals.pos_qty;
                    case 'sales_order_qty':
                        return totals.sales_order_qty;
                    case 'delivery_qty':
                        return totals.delivery_qty;
                    case 'total_qty':
                        return totals.total_qty;
                    case 'available_stocks':
                        return totals.available_stocks;
                    case 'incoming_stocks':
                        return totals.incoming_stocks;
                    case 'draft_incoming_stocks':
                        return totals.draft_incoming_stocks;
                    default:
                        return ''; // No data for other columns
                }
            });

        // Add totalsRow to the data array
        data.push(totalsRow);

        // Combine headers and data
        const worksheetData = [headers, ...data];

        // Create a worksheet
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

        // Create a new workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Daily Movement');

        // Generate an Excel file
        XLSX.writeFile(workbook, `dailyMovement-${formattedDate()}.xlsx`);
    };

    const handlePrint = () => {
        const printContent =
            document.getElementById('table-to-print').innerHTML;
        const newWindow = window.open('', '', 'width=800,height=600');
        newWindow.document.write(
            '<html><head><title>Print</title></head><body>'
        );
        newWindow.document.write(printContent);
        newWindow.document.write('</body></html>');
        newWindow.document.close();
        newWindow.print();
    };

    const toggleColumn = (columnName) => {
        setVisibleColumns((prevState) => ({
            ...prevState,
            [columnName]: !prevState[columnName],
        }));
    };

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    // Check if still loading or is checking the daily movement
    if (loading || isChecking) {
        return <Spinner />;
    }

    return (
        <section>
            <Show when="inv.dashboard.daily-movement">
                <div>
                    <SalesFilter
                        filterBy="daily_movement"
                        pageOrigin={pageOrigin}
                    />
                </div>

                <div className="ds-table-actions">
                    <button
                        className="small-button"
                        onClick={handleDownloadExcel}
                        title="Download Excel"
                    >
                        <i className="la la-cloud-download"></i>
                    </button>
                    <button
                        className="small-button"
                        onClick={handlePrint}
                        title="Print"
                    >
                        <i className="la la-print"></i>
                    </button>
                    <div className="dropdown">
                        <button
                            className="small-button"
                            onClick={toggleDropdown}
                            title="View Columns"
                        >
                            <i className="la la-columns"></i>
                        </button>
                        {dropdownOpen && (
                            <div className="dropdown-content">
                                {Object.keys(visibleColumns).map((column) => (
                                    <label key={column}>
                                        <input
                                            type="checkbox"
                                            checked={visibleColumns[column]}
                                            onChange={() =>
                                                toggleColumn(column)
                                            }
                                        />
                                        {column.replace('_', ' ')}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="ds-table-container">
                    <table
                        id="ds-table-to-print"
                        className="ds-table ds-table-striped"
                    >
                        <thead>
                            <tr>
                                {visibleColumns.product_name && (
                                    <th
                                        style={{ width: '150px' }}
                                        onClick={() =>
                                            handleSort('product_name')
                                        }
                                    >
                                        Name {getSortIcon('product_name')}
                                    </th>
                                )}
                                {visibleColumns.sku && (
                                    <th onClick={() => handleSort('sku')}>
                                        SKU {getSortIcon('sku')}
                                    </th>
                                )}
                                {visibleColumns.brand_name && (
                                    <th
                                        onClick={() => handleSort('brand_name')}
                                    >
                                        Brand {getSortIcon('brand_name')}
                                    </th>
                                )}
                                {visibleColumns.description && (
                                    <th
                                        style={{ width: '300px' }}
                                        onClick={() =>
                                            handleSort('description')
                                        }
                                    >
                                        Description {getSortIcon('description')}
                                    </th>
                                )}
                                {visibleColumns.supplier_name && (
                                    <th
                                        style={{ width: '150px' }}
                                        onClick={() => handleSort('supplier')}
                                    >
                                        Supplier {getSortIcon('supplier')}
                                    </th>
                                )}
                                {visibleColumns.pos_qty && (
                                    <th onClick={() => handleSort('pos_qty')}>
                                        POS <br /> Qty {getSortIcon('pos_qty')}
                                    </th>
                                )}
                                {visibleColumns.sales_order_qty && (
                                    <th
                                        onClick={() =>
                                            handleSort('sales_order_qty')
                                        }
                                    >
                                        SO <br /> Qty{' '}
                                        {getSortIcon('sales_order_qty')}
                                    </th>
                                )}
                                {visibleColumns.delivery_qty && (
                                    <th
                                        onClick={() =>
                                            handleSort('delivery_qty')
                                        }
                                    >
                                        Delivery <br /> Qty{' '}
                                        {getSortIcon('delivery_qty')}
                                    </th>
                                )}
                                {visibleColumns.total_qty && (
                                    <th onClick={() => handleSort('total_qty')}>
                                        Total <br /> Qty{' '}
                                        {getSortIcon('total_qty')}
                                    </th>
                                )}
                                {visibleColumns.available_stocks && (
                                    <th
                                        onClick={() =>
                                            handleSort('available_stocks')
                                        }
                                    >
                                        AVL <br /> Stocks{' '}
                                        {getSortIcon('available_stocks')}
                                    </th>
                                )}
                                {visibleColumns.incoming_stocks && (
                                    <th
                                        onClick={() =>
                                            handleSort('incoming_stocks')
                                        }
                                    >
                                        INC
                                        <br /> Stocks{' '}
                                        {getSortIcon('incoming_stocks')}
                                    </th>
                                )}
                                {visibleColumns.draft_incoming_stocks && (
                                    <th
                                        onClick={() =>
                                            handleSort('draft_incoming_stocks')
                                        }
                                    >
                                        Draft INC
                                        <br /> Stocks{' '}
                                        {getSortIcon('draft_incoming_stocks')}
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {daily_movement.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={
                                            Object.keys(visibleColumns).length
                                        }
                                        style={{
                                            textAlign: 'center',
                                            padding: '20px',
                                        }}
                                    >
                                        Sorry, there is no matching data to
                                        display
                                    </td>
                                </tr>
                            ) : (
                                daily_movement.map((movement) => (
                                    <tr
                                        key={movement._id}
                                        onClick={() =>
                                            handleRowClick(movement._id)
                                        }
                                    >
                                        {visibleColumns.product_name && (
                                            <td>
                                                <div className="td-value">
                                                    <Link
                                                        to={`/inventory/view/${movement._id}`}
                                                    >
                                                        {movement.product_name}
                                                    </Link>
                                                </div>
                                            </td>
                                        )}
                                        {visibleColumns.sku && (
                                            <td className="description-cell">
                                                <div className="td-value">
                                                    {movement.sku}
                                                </div>
                                            </td>
                                        )}
                                        {visibleColumns.brand_name && (
                                            <td className="description-cell">
                                                <div className="td-value">
                                                    {movement.brand_name ? (
                                                        <Link
                                                            to={`/inventory/products/brands/edit/${movement.brand_id}`}
                                                        >
                                                            {
                                                                movement.brand_name
                                                            }
                                                        </Link>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                        {visibleColumns.description && (
                                            <td className="description-cell">
                                                {movement.description || 'N/A'}
                                            </td>
                                        )}
                                        {visibleColumns.supplier_name && (
                                            <td>
                                                <div className="td-value">
                                                    {movement.supplier_name ? (
                                                        <Link
                                                            to={`/purchases/suppliers/edit/${movement.supplier_id}`}
                                                        >
                                                            {
                                                                movement.supplier_name
                                                            }
                                                        </Link>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                        {visibleColumns.pos_qty && (
                                            <td>
                                                <div className="td-value">
                                                    {NumberFormat(
                                                        movement.pos_qty.toFixed()
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                        {visibleColumns.sales_order_qty && (
                                            <td>
                                                <div className="td-value">
                                                    {NumberFormat(
                                                        movement.sales_order_qty.toFixed()
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                        {visibleColumns.delivery_qty && (
                                            <td>
                                                <div className="td-value">
                                                    {NumberFormat(
                                                        movement.delivery_qty.toFixed()
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                        {visibleColumns.total_qty && (
                                            <td>
                                                <div className="td-value">
                                                    {NumberFormat(
                                                        movement.total_qty.toFixed()
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                        {visibleColumns.available_stocks && (
                                            <td>
                                                <div className="td-value">
                                                    {movement.available_stocks !==
                                                        undefined &&
                                                    movement.available_stocks !==
                                                        null
                                                        ? new Intl.NumberFormat().format(
                                                              movement.available_stocks.toFixed(
                                                                  0
                                                              )
                                                          )
                                                        : 'N/A'}
                                                </div>
                                            </td>
                                        )}
                                        {visibleColumns.incoming_stocks && (
                                            <td>
                                                <div className="td-value">
                                                    {movement.incoming_stocks !==
                                                        undefined &&
                                                    movement.incoming_stocks !==
                                                        null
                                                        ? new Intl.NumberFormat().format(
                                                              movement.incoming_stocks.toFixed(
                                                                  0
                                                              )
                                                          )
                                                        : 'N/A'}
                                                </div>
                                            </td>
                                        )}{' '}
                                        {visibleColumns.draft_incoming_stocks && (
                                            <td>
                                                <div className="td-value">
                                                    {movement.draft_incoming_stocks !==
                                                        undefined &&
                                                    movement.draft_incoming_stocks !==
                                                        null
                                                        ? new Intl.NumberFormat().format(
                                                              movement.draft_incoming_stocks.toFixed(
                                                                  0
                                                              )
                                                          )
                                                        : 'N/A'}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                        <tfoot className="table-footer-totals">
                            <tr>
                                {visibleColumns.product_name && <td></td>}
                                {visibleColumns.sku && <td></td>}
                                {visibleColumns.brand_name && <td></td>}
                                {visibleColumns.description && <td></td>}
                                {visibleColumns.supplier_name && <td></td>}
                                {visibleColumns.pos_qty && (
                                    <td>{NumberFormat(totals.pos_qty)}</td>
                                )}
                                {visibleColumns.sales_order_qty && (
                                    <td>
                                        {NumberFormat(totals.sales_order_qty)}
                                    </td>
                                )}
                                {visibleColumns.delivery_qty && (
                                    <td>{NumberFormat(totals.delivery_qty)}</td>
                                )}
                                {visibleColumns.total_qty && (
                                    <td>{NumberFormat(totals.total_qty)}</td>
                                )}
                                {visibleColumns.available_stocks && (
                                    <td>
                                        {NumberFormat(totals.available_stocks)}
                                    </td>
                                )}
                                {visibleColumns.incoming_stocks && (
                                    <td>
                                        {NumberFormat(totals.incoming_stocks)}
                                    </td>
                                )}
                                {visibleColumns.draft_incoming_stocks && (
                                    <td>
                                        {NumberFormat(
                                            totals.draft_incoming_stocks
                                        )}
                                    </td>
                                )}
                            </tr>
                        </tfoot>
                    </table>
                </div>
                <div className="ds-table-footer">
                    <p>Total Documents: {pagination.totalItemsCount}</p>
                </div>
            </Show>
            <DailyMovementViewModal
                onHide={() => setModal('daily_movement_modal', null)}
            />
        </section>
    );
}

const mapStateToProps = (state) => ({
    daily_movement: state.daily_movement,
    muiPage: state.helper.muiPage,
});

export default connect(mapStateToProps, {
    getDailyMovement,
    dailyMovementChecker,
    sortTableByColumn,
    getSelectedRows,
    setModal,
})(DailyMovementTable);
